/**
 * SimulatorForm Component - UI Layer
 * Credit simulation form with amount, term, rate, and level inputs
 */

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Can } from '@/components/auth/Can';
import { UNE_PERM } from '@/constants';
import { useForm } from '@/hooks';
import type { SimulatorRequest } from '@/types/policy.types';
import type { SimulatorFormValues } from '../../types';

interface SimulatorFormProps {
  onSubmit: (data: SimulatorRequest) => void;
  isSubmitting: boolean;
}

const LEVEL_OPTIONS = [
  { value: '', label: 'Ninguno' },
  { value: 'BRONZE', label: 'Bronze' },
  { value: 'SILVER', label: 'Silver' },
  { value: 'GOLD', label: 'Gold' },
  { value: 'PLATINUM', label: 'Platinum' },
];

export function SimulatorForm({ onSubmit, isSubmitting }: SimulatorFormProps) {
  const { state, actions } = useForm<SimulatorFormValues>({
    initialValues: {
      amount: 5000,
      termDays: 84,
      interestRate: '',
      level: '',
    },
    validationRules: {
      amount: (v: unknown) => {
        const num = v as number;
        if (!num || num < 500) return 'Minimo $500';
        if (num > 50000) return 'Maximo $50,000';
        return undefined;
      },
      termDays: (v: unknown) => {
        const num = v as number;
        if (!num || num < 7) return 'Minimo 7 dias';
        if (num > 365) return 'Maximo 365 dias';
        return undefined;
      },
    },
    onSubmit: (values) => {
      const req: SimulatorRequest = {
        amount: values.amount,
        termDays: values.termDays,
        ...(values.interestRate !== '' && { interestRate: Number(values.interestRate) }),
        ...(values.level && { level: values.level }),
      };
      onSubmit(req);
    },
  });

  const amountError = state.touched.amount && state.errors.amount ? state.errors.amount : undefined;
  const termDaysError = state.touched.termDays && state.errors.termDays ? state.errors.termDays : undefined;

  return (
    <Card title="Parametros de Simulacion" description="Configura los valores del credito a simular">
      <form onSubmit={actions.handleSubmit} className="space-y-4">
        <Input
          label="Monto (MXN)"
          type="number"
          min={500}
          max={50000}
          step={100}
          required
          value={state.values.amount}
          onChange={(e) => actions.handleChange('amount', Number(e.target.value))}
          onBlur={() => actions.handleBlur('amount')}
          {...(amountError ? { error: amountError } : {})}
        />

        <Input
          label="Plazo (dias)"
          type="number"
          min={7}
          max={365}
          step={7}
          required
          value={state.values.termDays}
          onChange={(e) => actions.handleChange('termDays', Number(e.target.value))}
          onBlur={() => actions.handleBlur('termDays')}
          {...(termDaysError ? { error: termDaysError } : {})}
          hint="Multiplo de 7 dias (semanas)"
        />

        <Input
          label="Tasa anual (%)"
          type="number"
          min={0}
          max={200}
          step={0.1}
          value={state.values.interestRate}
          onChange={(e) =>
            actions.handleChange(
              'interestRate',
              e.target.value === '' ? '' : Number(e.target.value)
            )
          }
          onBlur={() => actions.handleBlur('interestRate')}
          hint="Si no se especifica, se usa la tasa del nivel"
        />

        <Select
          label="Nivel crediticio"
          options={LEVEL_OPTIONS}
          value={state.values.level as string}
          onChange={(val) => actions.handleChange('level', val)}
        />

        <Can permission={UNE_PERM.SIMULATOR_USE}>
          <Button type="submit" variant="primary" fullWidth isLoading={isSubmitting}>
            Simular Credito
          </Button>
        </Can>
      </form>
    </Card>
  );
}
